import { INameValue } from "src/shared/types/common.types";
import { TIconNames } from "src/shared/types/iconNames";

export type TransactionAgency = 0 | 1 | 2 | 3 | 4;

export const AGENCY_LABELS: Record<TransactionAgency, string> = {
  0: "Webcom",
  1: "Webcom RU",
  2: "",
  3: "iContext",
  4: "",
};

export type TransactionOperationType = "increase" | "decrease" | "transfer";

export enum TransactionStatus {
  IN_QUEUE = "in_queue",
  IN_PROCESS = "in_process",
  NOT_VERIFIED = "not_verified",
  COMPLETED = "completed",
  CLOSED_BY_GATE = "closed_by_gate",
  CLOSED_BY_FINANCE_SUPPORT = "closed_by_finance_support",
  NOT_COMPLETED = "not_completed",
}

export type TransactionSource = "ERP" | "ЛК";

export interface TransactionDto {
  id: string;
  agency: TransactionAgency;
  date: string;
  operationType: TransactionOperationType;
  clientName: string;
  contractNumber: string;
  accountNameFrom: string;
  serviceFrom: TransactionBackendService;
  accountNameTo: string;
  serviceTo: TransactionBackendService;
  amount: number;
  currency: string;
  status: TransactionStatus;
  originalStatus: TransactionOriginalStatus;
  source: TransactionSource;
}

export interface TransactionsListResponse {
  data: TransactionDto[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number | null;
    last_page: number;
    links: Array<{
      url: string | null;
      label: string;
      active: boolean;
    }>;
    path: string;
    per_page: number;
    to: number | null;
    total: number;
  };
}

export interface TransactionSort {
  field: "date";
  direction: "asc" | "ASC" | "desc" | "DESC";
}

export interface TransactionsQuery {
  page?: number;
  per_page?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
  operation_types?: TransactionOperationType[];
  agencies?: number[];
  client_ids?: number[];
  account_ids?: number[];
  statuses?: TransactionStatus[];
  sort?: TransactionSort;
}

/**
 * Row model for table UI
 */
export interface Transaction {
  id: string;
  agency: TransactionAgency;
  date: string;
  operationType: TransactionOperationType;
  clientName: string;
  contractNumber: string;
  from: string;
  fromIcon?: TIconNames;
  to: string;
  toIcon?: TIconNames;
  amount: number;
  currency: string;
  status: TransactionStatus;
  originalStatus: TransactionOriginalStatus;
  source: TransactionSource;
}

export interface TransactionDrawerSummary {
  id: string;
  clientName: string;
  contractNumber: string;
  accountName: string;
}

export type TransactionStepType =
  | "CHECK_BALANCE"
  | "INCREASE"
  | "DECREASE"
  | "ABORTION_INCREASE";

export type TransactionStepStatus = "success" | "error";

export interface TransactionStepDto {
  id: string;
  account_id: string;
  type: TransactionStepType;
  success: boolean;
  errors: string | null;
}

export interface TransactionDetailDto {
  external_id: string;
  status: TransactionStatus;
  steps: TransactionStepDto[];
}

export interface TransactionDetailResponse {
  data: TransactionDetailDto;
}

export interface TransactionStep {
  id: string;
  accountId: string;
  type: TransactionStepType;
  status: TransactionStepStatus;
  error: string | null;
}

export interface TransactionDetail {
  externalId: string;
  status: TransactionStatus;
  steps: TransactionStep[];
}

export interface TransactionDrawerSummary {
  id: string;
  clientName: string;
  contractNumber: string;
  accountName: string;
}

export const STEP_TYPE_LABEL: Record<TransactionStepType, string> = {
  CHECK_BALANCE: "Проверка баланса",
  INCREASE: "Зачисление",
  DECREASE: "Списание",
  ABORTION_INCREASE: "Отмена зачисления",
};

export const CLOSEABLE_STATUSES: TransactionStatus[] = [
  TransactionStatus.IN_QUEUE,
  TransactionStatus.NOT_VERIFIED,
];

export type TransactionFinalStatus = "SUCCESSFUL" | "FAILED";

export interface TransactionsCommonDropdownsResponse {
  data: {
    agencies: INameValue<number>[];
    transaction_types: INameValue<TransactionOperationType>[];
    transaction_short_statuses: INameValue<TransactionStatus>[];
  };
}

export interface TransactionClientsSearchRequest {
  search: string;
  date_from?: string;
  date_to?: string;
  account_ids?: number[];
  agencies?: number[] | null;
}

export interface TransactionAccountsSearchRequest {
  search: string;
  date_from?: string;
  date_to?: string;
  client_ids?: number[];
  agencies?: number[] | null;
}

export interface TransactionDropdownSearchResponse {
  data: INameValue<number>[];
}

export type TransactionBackendService =
  | "google"
  | "yandex"
  | "tiktok"
  | "fb"
  | "telegram"
  | "vkads"
  | "undefined";

export interface TransactionIdsSelectRequest {
  ids: number[];
}

export type TransactionOriginalStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "VERIFICATION"
  | "DECREASE_VERIFICATION"
  | "ABORTION_IN_PROGRESS"
  | "ABORTION_VERIFICATION"
  | "ABORTION_FAILED"
  | "UNVERIFIED"
  | "DECREASE_UNVERIFIED"
  | "ABORTION_UNVERIFIED"
  | "MANUAL_VERIFICATION"
  | "SUCCESSFUL"
  | "CLOSED_MANUALLY"
  | "CLOSED_BY_AGENCY"
  | "CANCELLED"
  | "FAILED"
  | "ABORTED"
  | "ERROR";

export type TransactionOriginalStatusMeta = {
  label: string;
  erpDescription: string;
  canBeClosed: boolean;
};

export type TransactionsExportResponse = {
  data: {
    message: string | null;
  };
};
